import { useEffect, useState, type CSSProperties } from "react";
import { stepProductFocus } from "../lib/product-focus";
import { useExperienceStore } from "../store/experience-store";
import "./experience-controls.css";

export function ExperienceControls({ onToggleAutoPlay }: Readonly<{
  onToggleAutoPlay: () => void;
}>): React.JSX.Element {
  const chapter = useExperienceStore((state) => state.chapterIndex);
  const autoPlay = useExperienceStore((state) => state.autoPlay);
  const explosion = useExperienceStore((state) => state.explosion);
  const focused = useExperienceStore((state) => state.focusedProductIndex);
  const setExplosion = useExperienceStore((state) => state.setExplosion);
  const setFocus = useExperienceStore((state) => state.setFocusedProductIndex);
  const rotation = useExperienceStore((state) => state.heroRotation);
  const scanPosition = useExperienceStore((state) => state.scanPosition);
  const [fullscreen, setFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState(false);
  useEffect(() => {
    const update = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
      setFullscreenError(false);
    } catch { setFullscreenError(true); }
  };

  return (
    <div className="experience-controls">
      <button type="button" data-playback-toggle="true" onClick={onToggleAutoPlay}
        aria-label={autoPlay ? "暂停自动播放" : "开始自动播放"}
        title={autoPlay ? "暂停自动播放" : "开始自动播放"}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          {autoPlay ? <path d="M9 5v14M15 5v14" /> : <path d="m8 5 11 7-11 7Z" />}
        </svg>
      </button>
      {chapter !== 4 && <div className="product-controls">
        <button type="button" aria-label="向左旋转模型" title="向左旋转模型" onClick={() => useExperienceStore.getState().setHeroRotation({ ...rotation, y: rotation.y - Math.PI / 6 })}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 11a8 8 0 1 1 3 7M4 4v7h7" /></svg>
        </button>
        <button type="button" aria-label="复位模型视角" title="复位模型视角（也可双击画面）" disabled={rotation.x === 0 && rotation.y === 0} onClick={() => useExperienceStore.getState().setHeroRotation({ x: 0, y: 0 })}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 8 4v10l-8 4-8-4V7Zm0 9v9M4 7l8 5 8-5" /></svg>
        </button>
        <button type="button" aria-label="向右旋转模型" title="向右旋转模型" onClick={() => useExperienceStore.getState().setHeroRotation({ ...rotation, y: rotation.y + Math.PI / 6 })}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11a8 8 0 1 0-3 7M20 4v7h-7" /></svg>
        </button>
      </div>}
      {chapter === 2 && <div className="assembly-control" key="scan">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3H3v5M19 3h2v5M3 16v5h2M21 16v5h-2M1 12h22" /></svg>
        <input type="range" min="0" max="1" step="0.01" value={scanPosition ?? 0.5} aria-label="扫描截面位置" title="拖动扫描截面"
          style={{ "--assembly-amount": scanPosition ?? 0.5 } as CSSProperties}
          onChange={(event) => useExperienceStore.getState().setScanPosition(Number(event.target.value))} />
        <button type="button" aria-label="自动扫描" title="恢复自动扫描" disabled={scanPosition === null} onClick={() => useExperienceStore.getState().setScanPosition(null)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 5 11 7-11 7Z" /></svg>
        </button>
      </div>}
      {chapter === 3 && (
        <div className="assembly-control" key="assembly">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 8h14v8H5zM9 5v14M15 5v14" /></svg>
          <input type="range" min="0" max="1" step="0.01" value={explosion}
            aria-label="装配展开程度" title="拖动调整装配展开程度"
            style={{ "--assembly-amount": explosion } as CSSProperties}
            onChange={(event) => setExplosion(Number(event.target.value))} />
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6v12M21 6v12M8 8h8v8H8zM1 12h5M18 12h5" /></svg>
        </div>
      )}
      {chapter === 4 && (
        <div className="product-controls" key="products">
          <button type="button" aria-label="上一个产品" title="上一个产品"
            onClick={() => setFocus(stepProductFocus(focused, -1))}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14 6-6 6 6 6" /></svg>
          </button>
          <button type="button" aria-label="复位产品聚焦" title="复位产品聚焦"
            disabled={focused === null} onClick={() => setFocus(null)}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 3H3v5M16 3h5v5M3 16v5h5M21 16v5h-5M9 9l6 6m0-6-6 6" /></svg>
          </button>
          <button type="button" aria-label="下一个产品" title="下一个产品"
            onClick={() => setFocus(stepProductFocus(focused, 1))}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m10 6 6 6-6 6" /></svg>
          </button>
        </div>
      )}
      {document.fullscreenEnabled && <button type="button" aria-label={fullscreen ? "退出全屏" : "进入全屏"} title={fullscreen ? "退出全屏" : "进入全屏"} onClick={toggleFullscreen}>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d={fullscreen ? "M3 8h5V3M21 8h-5V3M8 21v-5H3M16 21v-5h5" : "M8 3H3v5M16 3h5v5M3 16v5h5M21 16v5h-5"} /></svg>
      </button>}
      {fullscreenError && <span role="status" className="control-error">无法进入全屏，请重试。</span>}
    </div>
  );
}
